import React from 'react'
import { useNavigate } from 'react-router-dom'
import toastr from 'toastr'
import { getHomeAwayStatus, getMatchOutcomeForPlayer } from '../../utils/match'
import './OpponentCard.css'

/**
 * OpponentCard - Simple roster card for displaying division opponents
 *
 * This is a roster/directory component showing opponent info.
 * Primary interaction: Click card to view opponent's profile
 * Secondary actions: Message, call, email, and view court location buttons
 *
 * Props:
 * - opponent: Player object with id, first_name, last_name, home_court
 * - partner: Optional second player object for doubles pairs (same shape as opponent)
 * - onMessage: Function to handle chat messaging when the message button is clicked
 * - leagueId: League ID for navigation context (optional)
 * - divisionId: Division ID for navigation context (optional)
 * - currentPlayerId: Current player's ID to determine home/away status (optional)
 * - match: Match object to determine home/away relationship (optional)
 */
export default function OpponentCard({
  opponent,
  partner = null,
  onMessage,
  leagueId,
  divisionId,
  currentPlayerId,
  match,
}) {
  const navigate = useNavigate()

  // Determine if current player is home or away (if we have match data)
  const homeAwayStatus = getHomeAwayStatus(match, currentPlayerId)
  const isCurrentPlayerHome = homeAwayStatus.isHome
  const showHomeAwayIndicator = homeAwayStatus.shouldShow && Boolean(match)
  const matchOutcome =
    match && currentPlayerId
      ? getMatchOutcomeForPlayer(match, currentPlayerId)
      : 'unknown'
  const showOutcomeBadge = matchOutcome === 'win' || matchOutcome === 'loss'

  const handleCardClick = () => {
    const navigationState = leagueId
      ? { fromLeague: true, leagueId, divisionId }
      : undefined
    navigate(`/players/${opponent.id}/history`, { state: navigationState })
  }

  const handleChatClick = (e) => {
    e.stopPropagation()
    if (onMessage) {
      onMessage(opponent)
    }
  }

  const handleCall = (e) => {
    e.stopPropagation()
    if (opponent.phone) {
      window.location.href = `tel:${opponent.phone}`
    } else {
      toastr.warning(
        `Phone number not available for ${opponent.first_name} ${opponent.last_name}`,
      )
    }
  }

  const handleEmail = (e) => {
    e.stopPropagation()
    // Prefer email on the user object, but fall back to opponent.email if needed
    const email = opponent.user?.email || opponent.email
    if (email) {
      window.location.href = `mailto:${email}`
    } else {
      toastr.warning(
        `Email address not available for ${opponent.first_name} ${opponent.last_name}`,
      )
    }
  }

  const handlePartnerClick = (e) => {
    e.stopPropagation()
    const navigationState = leagueId
      ? { fromLeague: true, leagueId, divisionId }
      : undefined
    navigate(`/players/${partner.id}/history`, { state: navigationState })
  }

  const handleCourtClick = (e) => {
    e.stopPropagation()
    if (opponent.home_court?.address) {
      // Open Google Maps with the court address
      const addressQuery = encodeURIComponent(opponent.home_court.address)
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${addressQuery}`,
        '_blank',
        'noopener,noreferrer',
      )
    } else if (opponent.home_court?.name) {
      // Fallback to searching by court name
      const nameQuery = encodeURIComponent(opponent.home_court.name)
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${nameQuery}`,
        '_blank',
        'noopener,noreferrer',
      )
    }
  }

  const courtName = opponent.home_court?.name
  const hasCourtLocation =
    opponent.home_court?.address || opponent.home_court?.name

  return (
    <div
      className="opponent-card"
      data-testid="opponent-card"
      onClick={handleCardClick}
      tabIndex={0}
      onKeyDown={(e) => {
        // Only handle Enter/Space on the card itself, not on buttons
        if (
          (e.key === 'Enter' || e.key === ' ') &&
          e.target.tagName !== 'BUTTON'
        ) {
          e.preventDefault()
          handleCardClick()
        }
      }}
    >
      <div className="opponent-card-left">
        {/* Avatar */}
        <div className="opponent-avatar">
          {opponent.first_name?.[0]}
          {opponent.last_name?.[0]}
          {partner && <span aria-hidden="true">+</span>}
        </div>

        {/* Opponent info */}
        <div className="opponent-info">
          <div className="opponent-name-row">
            <div
              className="opponent-name"
              title={
                partner
                  ? `${opponent.first_name} ${opponent.last_name} & ${partner.first_name} ${partner.last_name}`
                  : `${opponent.first_name} ${opponent.last_name}`
              }
            >
              {opponent.first_name} {opponent.last_name}
              {partner && (
                <span
                  className="opponent-partner-name"
                  onClick={handlePartnerClick}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handlePartnerClick(e)
                    }
                  }}
                >
                  {' & '}
                  {partner.first_name} {partner.last_name}
                </span>
              )}
            </div>
          </div>
          <div className="opponent-meta">
            {showOutcomeBadge && (
              <span
                className={`opponent-outcome-badge ${matchOutcome === 'win' ? 'outcome-win' : 'outcome-loss'}`}
                aria-label={
                  matchOutcome === 'win'
                    ? 'You won this match'
                    : 'You lost this match'
                }
                title={
                  matchOutcome === 'win'
                    ? 'You won this match'
                    : 'You lost this match'
                }
              >
                {matchOutcome === 'win' ? 'Win' : 'Loss'}
              </span>
            )}
            {showHomeAwayIndicator && (
              <span
                className={`home-away-badge ${isCurrentPlayerHome ? 'badge-home' : 'badge-away'}`}
                title={
                  isCurrentPlayerHome
                    ? 'You host this match'
                    : 'You travel to their court'
                }
                aria-label={isCurrentPlayerHome ? 'Home match' : 'Away match'}
              >
                {isCurrentPlayerHome ? '🏠 Home' : '🏃 Away'}
              </span>
            )}
            {courtName && (
              <button
                className="meta-location-link"
                onClick={handleCourtClick}
                disabled={!hasCourtLocation}
                aria-label={`View ${courtName} on map`}
                title={
                  hasCourtLocation ? 'View court location on map' : courtName
                }
              >
                📍 {courtName}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="opponent-card-right">
        {/* Three action buttons - always visible */}
        <button
          className="action-btn"
          onClick={handleCall}
          aria-label={`Call ${opponent.first_name} ${opponent.last_name}`}
          title="Call"
        >
          📞
        </button>
        <button
          className="action-btn"
          onClick={handleEmail}
          aria-label={`Email ${opponent.first_name} ${opponent.last_name}`}
          title="Email"
        >
          ✉️
        </button>
        <button
          className="action-btn"
          onClick={handleChatClick}
          aria-label={`Message ${opponent.first_name} ${opponent.last_name}`}
          title="Send message"
        >
          💬
        </button>
      </div>
    </div>
  )
}
